import { useMediaMatch, useScroll } from "@sciam/shared";
import { useState } from "react";
import { cx } from "~utils";

import InjectorAd from "~/features/ads/InjectorAd";
import { articleInjector, prepareInjector } from "~/features/ads/injector";
import { emailCollectorCookie } from "~/features/paywall/constants";
import { useRecommendations } from "~/features/recommendations/ArticleRecommendations";
import { useComponentBlocks } from "~blocks/use-component-blocks";
import { useAuth } from '~features/auth';
import { useOverlay } from "~features/paywall/useOverlay";
import { useContentSize } from "./hooks/use-content-size";

import PodcastStickyFooter from "~features/article/PodcastStickyFooter";
import { RecommendationsTest } from "./RecommendationsTest";
import ArticleBody from "./body/ArticleBody";
import ArticleFooter from "./footer/ArticleFooter";
import ArticleHeader from "./header/ArticleHeader";

import ArticleListHeader from "~/features/article-list/Header";
import ArticleList from "~features/article-list/ArticleList";

import { ColumnSignup } from "~features/blocks/cta/ColumnSignup";
import { PodcastSubscribeBlock } from "./PodcastSubscribeBlock";

import styles from "./Article.module.css";

export default function Article({
  article,
  inject = true,
  readTime = null,
  wordCount = null,
  isSponsored = false,
  podcastSeriesInfo = null,
}) {
  // This is used during injection to determine whether to inject Newsletter CTAs
  const { isMaybeLoggedIn } = useAuth()
  const hasEmailCollectorRegistered =
    typeof window === "undefined" ? false : document?.cookie.includes(emailCollectorCookie + "=1");
  const userEmailAlreadyCollected = isMaybeLoggedIn || hasEmailCollectorRegistered;

  // Whether the paywall modal is active
  const { enabled: paywallActive } = useOverlay();

  // Reflow the injector when the user crosses a major threshold
  const isDesktopLayout = useMediaMatch("(min-width: 700px)");

  // Calculate content size for ad injection
  //
  // These values are used to estimate the height of text blocks
  // to optimize ad placement. For example, on mobile we have more
  // vertical space to work with, so we can inject ads more frequently.
  const { charsPerLine, contentWidth } = useContentSize({
    contentGutter: 20 * 2,
    minViewportWidth: 375, // Virtually no user has a viewport smaller than this
    maxContentWidth: 700, // .article__block max-width
    maxCharsPerLine: 75, // Loose estimate but accurate enough for our purposes
    // minCharsPerLine: 50, // Unused until we need to support dynamic font sizes
  });

  // Count the number of words in the article.  700 words is a rough estimate of a short article to decide
  // if the newsletter sign up module should appear on top.
  const placeNewsletterSignUpOnTop = wordCount < 700 && !userEmailAlreadyCollected;

  const contentBlocks = useComponentBlocks(article.content, {
    prepare: prepareInjector,
    inject(blocks) {
      if (!inject) return blocks;

      const blocksWithAdsInjected = articleInjector(blocks, {
        charsPerLine,
        contentWidth,
        allowNewsletterCta: !userEmailAlreadyCollected && !placeNewsletterSignUpOnTop,
      });

      return blocksWithAdsInjected;
    },
    injectDeps: [isDesktopLayout, inject],
  });

  const recommendations = useRecommendations();
  const { media_url, media_type, digital_column } = article;
  const isPodcastPage = media_url && media_type === "podcast";
  // Default CTA and newsletter names if the article is not part of a column
  let signupCta = article.digital_column_signup_cta
    ? article.digital_column_signup_cta
    : "Sign Up for Our Free Daily Newsletter";
  let newsletterName = article.digital_column_newsletter_name
    ? article.digital_column_newsletter_name
    : "today_in_science";

  return (
    <>
      <ArticleRoot article={article} isSponsored={isSponsored}>
        <RecommendationsTest />
        <ArticleHeader article={article} readTime={readTime} isSponsored={isSponsored} />
        {isPodcastPage && <PodcastSubscribeBlock podcastSeriesInfo={podcastSeriesInfo} />}
        {(digital_column || placeNewsletterSignUpOnTop) && (
          <ColumnSignup
            signup_cta={signupCta}
            newsletter_name={newsletterName}
            column={digital_column}
            signupHeaderText={"Join Our Community of Science Lovers!"}
          />
        )}
        <ArticleBody
          content={contentBlocks}
          isBlogPost={article.isBlogPost || article.subtype === "BlogPost"}
          splitAtBlock={article.paywall_exempt ? null : 3} // content after this index will be hidden while paywall is active
          hideContentAfterSplit={paywallActive && !isSponsored} // whether to render content after the page break
          showSplitCta={!inject && !article.paywall_exempt && !isSponsored} // whether to mask content from browser Reader View features
        />
        <ArticleFooter article={article} isSponsored={isSponsored} />
        <InjectorAd position="article-footer" />
      </ArticleRoot>
      {isPodcastPage && <PodcastStickyFooter article={article} />}
      {!isSponsored && recommendations.popular?.length && (
        <>
          <ArticleListHeader title="Popular Stories" />
          <ArticleList
            results={recommendations.popular}
            clickTrackKey={"popularmodule-"}
            ads={false}
            maxExpectedItems={6}
          />
        </>
      )}
    </>
  );
}

function ArticleRoot({ article, children, isSponsored }) {
  const isOpinion = !!article.is_opinion;

  // Rather than using a hook that returns scroll position state, the `useScroll` hook accepts a
  // callback function that execute on scroll.
  // This gives us total control over when the component re-renders.
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  useScroll(({ y: scrollY }) => setIsScrolledDown(scrollY > 500));

  return (
    <article
      className={cx(
        styles.article,
        isOpinion && styles.opinion,
        isSponsored && styles.sponsored,
        isScrolledDown && styles.scrolled,
      )}
    >
      {children}
    </article>
  );
}
